import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DoenKidsGenericApiProvider } from 'src/providers/generic.provider';
import { IServiceResponse } from 'typings/api-generic';
import {
  IAiAdjustmentAvailabilityResponse,
  IAiAdjustmentRequest,
  IAiAdjustmentResponse,
} from 'typings/api-activity';

@Injectable({
  providedIn: 'root',
})
export class AiAdjustmentService {
  constructor(private $baseApi: DoenKidsGenericApiProvider) {}

  /**
   * Reports whether an adjustment is allowed right now and how much budget is left.
   * Never charges anything and never creates a quota window.
   */
  async availability(organizationUnitId: number, activityId: number) {
    // genericGetCallWithoutErrorHandler takes no params object, so the query
    // travels in the url. Without the error handler, a 403 from the permission
    // gates reaches the caller instead of becoming a toast: the caller decides
    // whether that is worth showing.
    //
    const url = `/activity/ai-adjustment/availability?organization_unit_id=${organizationUnitId}&activity_id=${activityId}`;

    return (await firstValueFrom(this.$baseApi.genericGetCallWithoutErrorHandler(url))) as IAiAdjustmentAvailabilityResponse;
  }

  /**
   * Requests an adjustment proposal. Deliberately without the generic error
   * handler: a guardrail denial and a provider failure carry the detail the
   * caller needs to pick one of the four error states, and the generic handler
   * would swallow it into a single toast.
   *
   * Takes 15-60+ seconds; the API's own upstream timeout is 180 seconds.
   */
  async adjust(request: IAiAdjustmentRequest) {
    return (await firstValueFrom(this.$baseApi.genericPostCallWithoutErrorHandler('/activity/ai-adjustment', request))) as IAiAdjustmentResponse;
  }

  /**
   * Records that the user dismissed a proposal. Only for a proposal the user
   * actually saw: a denied or failed attempt is already ledgered server side.
   *
   * Idempotent server side, so a replay records nothing and still answers 200.
   */
  async reject(aiAdjustmentId: number) {
    return (await firstValueFrom(
      this.$baseApi.genericPostCallWithoutErrorHandler('/activity/ai-adjustment/reject', { ai_adjustment_id: aiAdjustmentId }),
    )) as IServiceResponse;
  }
}
